<template>
  <div :id="id" ref="alertBanner" role="alert" :class="bannerClass">
    <div :class="fullPageBanner ? 'text-center' : 'flex items-center'">
      <span v-if="hasIcon" class="pr-4">
        <slot name="icon">
          <WarningOutlineIcon v-if="icon == 'alert'" :color="exclamationColor" />
          <TickCircleOutlineIcon v-if="icon == 'success'" color="#155A32" />
          <TrustAndSafetyIcon v-if="icon == 'trust'" color="#005988" />
          <PasswordHideOutlineIcon v-if="icon == 'inactive'" color="#644900" />
          <MessageSentOutlineIcon v-if="icon == 'recruit'" />
        </slot>
      </span>
      <span id="banner-message" :class="spanClasses" @keyup.enter="redirect" @click="redirect">
        <slot></slot>
      </span>
    </div>
    <button v-if="dismissable" class="focus:outline-brand-contrast" @click="hideBanner">
      <span class="sr-only">Close Banner Button</span>
      <CloseIcon color="#4B4C52" />
    </button>
  </div>
</template>

<script lang="ts">
import WarningOutlineIcon from '../../graphics/icons/WarningOutlineIcon.vue';
import TickCircleOutlineIcon from '../../graphics/icons/TickCircleOutlineIcon.vue';
import TrustAndSafetyIcon from '../../graphics/icons/TrustAndSafetyIcon.vue';
import PasswordHideOutlineIcon from '../../graphics/icons/PasswordHideOutlineIcon.vue';
import MessageSentOutlineIcon from '../../graphics/icons/MessageSentOutlineIcon.vue';
import helperStatus from '../../helpers/status';
import { redirect } from '../../helpers/redirects';
import CloseIcon from '../../graphics/icons/CloseIcon.vue';

export default {
  name: 'AlertBanner',
  components: {
    WarningOutlineIcon,
    TickCircleOutlineIcon,
    TrustAndSafetyIcon,
    PasswordHideOutlineIcon,
    MessageSentOutlineIcon,
    CloseIcon,
  },
  props: {
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Warn,
      required: false,
    },
    href: {
      type: String,
      default: '',
    },
    dismissable: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    fullPageBanner: {
      type: Boolean,
      default: false,
    },
    inlineBanner: {
      type: Boolean,
      default: false,
    },
    autoScrollToBanner: {
      type: Boolean,
      default: true,
    },
  },
  data(): any {
    return {
      helperStatus,
    };
  },
  computed: {
    hasIcon(): Boolean {
      const iconSlot = this.$slots.icon?.();
      const hasIconSlot: boolean = !!(iconSlot && iconSlot.length > 0);
      return !this.fullPageBanner && (this.status !== helperStatus.Neutral || hasIconSlot);
    },
    icon(): String {
      if (this.status === helperStatus.Valid) return 'success';
      if (this.status === helperStatus.Trust) return 'trust';
      if (this.status === helperStatus.Inactive) return 'inactive';
      if (this.status === helperStatus.Recruit) return 'recruit';
      return 'alert';
    },
    bannerClassStatus(): String {
      if (this.status === helperStatus.Error) return 'banner-error';
      if (this.status === helperStatus.Trust) return 'banner-trust';
      if (this.status === helperStatus.Recruit) return 'banner-recruit';
      if (this.status === helperStatus.Neutral) return 'banner-pink';
      if (this.status === helperStatus.Warn || this.status === helperStatus.Inactive) return 'banner-warn';
      if (this.fullPageBanner) return 'banner-pink';
      return 'banner-success';
    },
    bannerClass(): String {
      let defaultClass = ' py-8 px-16 items-center flex justify-between text-strong-body';
      if (this.fullPageBanner) {
        defaultClass = ' text-strong-body';
      } else if (this.inlineBanner) {
        defaultClass = ' items-center flex justify-between text-strong-body p-16 md:pl-28 rounded-xl';
      }
      return this.bannerClassStatus + defaultClass;
    },
    spanClasses(): String {
      const baseClasses = this.hasIcon ? 'mx-8' : '';
      if (this.href) return `${baseClasses} cursor-pointer`.trim();
      return baseClasses;
    },
    exclamationColor(): String | undefined {
      return this.status === helperStatus.Warn ? '#644900' : undefined;
    },
  },
  mounted() {
    if (this.autoScrollToBanner) {
      this.scrollToBanner();
    }
  },
  methods: {
    redirect() {
      if (this.href) {
        redirect(this.href);
      }
    },
    hideBanner() {
      this.$emit('show', false);
    },
    scrollToBanner() {
      this.$refs.alertBanner.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const offset = -200; // we can make this value adjustable if we need to
      window.scrollTo(0, this.$refs.alertBanner.offsetTop + offset);
    },
  },
};
</script>

<style scoped lang="scss">
.banner-error a,
.banner-success a,
.banner-warn a {
  text-decoration: underline;
  &:focus {
    outline-color: theme('colors.brand.contrast');
  }
}
.banner-error {
  background: theme('colors.critical.subdued');
  color: theme('colors.critical.contrast');
}
.banner-success {
  background: theme('colors.success.subdued');
  color: theme('colors.success.contrast');
}
.banner-warn {
  background: theme('colors.warning.subdued');
  color: theme('colors.warning.contrast');
}
.banner-trust {
  background: theme('colors.safety.subdued');
  color: theme('colors.safety.contrast');
}
.banner-pink {
  background: theme('colors.primary.subdued');
  color: theme('colors.primary.DEFAULT');
}
.banner-recruit {
  background: theme('colors.primary.subdued');
  color: theme('colors.primary.DEFAULT');
}
</style>
